import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import ArtifactTable from "../../components/ArtifactTable";
import { MODULES } from "../../config/modules";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sparkles } from "lucide-react";

export default function MeetingsPage() {
  const { projectId } = useParams();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const extract = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/generate`, {
        generationType: "meetingminutes",
        context: { meetingNotes: notes },
      });
      setResult(data.result);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const apply = async () => {
    setApplying(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      let count = 0;
      for (const d of result.decisions || []) {
        await api.post(`/projects/${projectId}/artifacts/action_decision_log`, {
          date: d.date || today, actionOrDecision: `Decision: ${d.description}`,
          type: "D", owner: d.owner || "", dueDate: d.date || today, status: "Closed",
        });
        count++;
      }
      for (const a of result.actions || []) {
        await api.post(`/projects/${projectId}/artifacts/action_decision_log`, {
          date: today, actionOrDecision: a.description,
          type: "A", owner: a.owner || "", dueDate: a.dueDate || "", status: "Open",
        });
        count++;
      }
      toast.success(`${count} entries added to the Action & Decision Log`);
      setOpen(false); setNotes(""); setResult(null);
    } catch (e) { toast.error(errMsg(e)); }
    setApplying(false);
  };

  return (
    <div data-testid="meetings-page">
      <div className="border border-[#1E6FE8]/30 bg-[#1E6FE8]/5 p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline-label text-[#1E6FE8]">AI Minutes Extraction</div>
          <p className="text-sm mt-0.5 max-w-xl">Paste raw meeting notes or a transcript — the AI extracts decisions and actions straight into the Action & Decision Log.</p>
        </div>
        <Button data-testid="open-minutes-ai-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={() => setOpen(true)}>
          <Sparkles className="h-4 w-4 mr-1" /> Extract from notes
        </Button>
      </div>

      <ArtifactTable projectId={projectId} type="meetings" config={MODULES.meetings} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">Meeting Minutes → Actions & Decisions</DialogTitle></DialogHeader>
          <Textarea data-testid="minutes-notes-input" rows={8} placeholder={"Paste meeting notes or transcript here…\ne.g. 'Sarah agreed the go-live moves to 26 Feb. John to profile legacy extracts by 10 Oct.'"}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Button data-testid="extract-minutes-btn" className="rounded-none" onClick={extract} disabled={busy || !notes.trim()}>
            <Sparkles className="h-4 w-4 mr-1" />{busy ? "Extracting…" : "Extract with AI"}
          </Button>

          {result && (
            <div data-testid="minutes-result" className="space-y-3 text-sm">
              {result.summary && (
                <div className="border border-border p-3 bg-muted/50">
                  <div className="overline-label">Summary</div>
                  <p className="mt-1">{result.summary}</p>
                </div>
              )}
              <div>
                <div className="overline-label mb-1">Decisions ({(result.decisions || []).length})</div>
                {(result.decisions || []).map((d, i) => (
                  <div key={i} className="border-l-2 border-[#00C781] pl-3 py-1">
                    {d.description}
                    <span className="text-xs text-muted-foreground ml-2">{[d.owner, d.date].filter(Boolean).join(" · ")}</span>
                  </div>
                ))}
                {(result.decisions || []).length === 0 && <div className="text-xs text-muted-foreground">None found.</div>}
              </div>
              <div>
                <div className="overline-label mb-1">Actions ({(result.actions || []).length})</div>
                {(result.actions || []).map((a, i) => (
                  <div key={i} className="border-l-2 border-[#FFB300] pl-3 py-1">
                    {a.description}
                    <span className="text-xs text-muted-foreground ml-2">{[a.owner, a.dueDate && `due ${a.dueDate}`].filter(Boolean).join(" · ")}</span>
                  </div>
                ))}
                {(result.actions || []).length === 0 && <div className="text-xs text-muted-foreground">None found.</div>}
              </div>
              <Button data-testid="apply-minutes-btn" className="rounded-none w-full bg-[#00C781] hover:bg-[#00a86b] text-black" onClick={apply}
                disabled={applying || ((result.decisions || []).length === 0 && (result.actions || []).length === 0)}>
                {applying ? "Adding…" : `Add ${(result.decisions || []).length + (result.actions || []).length} entries to Action & Decision Log`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
